import { api, oneSpec, useAuthStore } from '@bemor/web-kit';
import {
    AsOfBadge,
    Badge,
    Button,
    Checkbox,
    ConfirmDialog,
    DateTime,
    GraphCanvas,
    KeyValueList,
    Label,
    Money,
    PageHeader,
    ReasonDialog,
    Section,
    StatusBadge,
} from '@bemor/web-kit/ui';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import type { RiskKey, RiskSignal } from '@/api/types';
import { DetailBody } from '@/components/detail';
import { P } from '@/lib/permissions';
import { useOne, useWrite } from '@/lib/queries';
import { useMe, who } from '@/lib/staff';
import { ADMIN_STATUSES } from '@/lib/statuses';
import { keyHref } from '@/pages/risk/case';
import { caseHref } from '@/pages/risk/reviews';

/** The reads of `/risk/keys/:kind/:key` (BM-46 §2.0). */
export const riskKeyReads = (
    kind: string | undefined,
    key: string | undefined,
) => ({
    key: oneSpec(
        `/staff/risk/keys/${kind ?? ''}/${encodeURIComponent(key ?? '')}`,
    ),
});

const KIND_LABEL: Record<string, string> = {
    email: 'Email',
    payment_account: 'Card',
    ip: 'IP address',
    device: 'Device',
    account: 'Account',
    bin: 'BIN',
};

/**
 * BM-125 decisions 21 and 22: one hashed buyer key, its counters and flag, the graph around it and its cases. Staff
 * with risk.buyers.flag flag it (with a reason, spreading to linked keys unless unticked) or clear the flag.
 */
export function RiskKeyPage() {
    const { kind = '', key = '' } = useParams();
    const navigate = useNavigate();
    const can = useAuthStore((s) => s.can);
    const me = useMe();
    const path = riskKeyReads(kind, key).key.path;
    const query = useOne<RiskKey>(path);
    const [dialog, setDialog] = useState<'flag' | 'clear' | null>(null);
    const [spread, setSpread] = useState(true);
    const flag = useWrite(
        async (body: { reason: string | null; spread: boolean }) =>
            (
                await api().post<RiskSignal>(
                    `/staff/buyers/${encodeURIComponent(key)}/flag`,
                    { kind, reason: body.reason, spread: body.spread },
                )
            ).data,
        [path, '/staff/risk/cases'],
    );
    const label = KIND_LABEL[kind] ?? kind;
    return (
        <div>
            <PageHeader
                crumbs={[{ label: 'Review', href: '/risk/reviews' }]}
                title={`${label} key`}
                meta={
                    <>
                        <span className="font-mono text-xs break-all">
                            {key}
                        </span>
                        {query.data?.data.on_shared_list ? (
                            <Badge
                                variant="warning"
                                data-testid="key-shared-list"
                            >
                                Shared list
                            </Badge>
                        ) : null}
                        <AsOfBadge asOf={query.freshness.asOf} />
                    </>
                }
                primaryAction={
                    can(P.buyersFlag) && query.data ? (
                        query.data.data.signal?.flagged_at ? (
                            <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setDialog('clear')}
                            >
                                Clear flag
                            </Button>
                        ) : (
                            <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => {
                                    flag.reset();
                                    setSpread(true);
                                    setDialog('flag');
                                }}
                            >
                                Flag
                            </Button>
                        )
                    ) : undefined
                }
            />
            <DetailBody query={query} title="Buyer key">
                {(k) => (
                    <div className="space-y-4">
                        <Section title="Counters and flag">
                            {k.signal ? (
                                <KeyValueList
                                    items={[
                                        {
                                            label: 'Flag',
                                            value: k.signal.flagged_at ? (
                                                <span className="flex flex-wrap items-center gap-2">
                                                    <Badge variant="danger">
                                                        Flagged
                                                    </Badge>
                                                    {k.signal.flag_reason}
                                                    {k.signal.flag_spread ===
                                                    false ? (
                                                        <span className="text-fg-2">
                                                            (does not spread)
                                                        </span>
                                                    ) : null}
                                                </span>
                                            ) : (
                                                'Not flagged'
                                            ),
                                        },
                                        k.signal.flagged_at
                                            ? {
                                                  label: 'Flagged by',
                                                  value: (
                                                      <>
                                                          {who(
                                                              k.signal
                                                                  .flagged_by,
                                                              me,
                                                          )}
                                                          ,{' '}
                                                          <DateTime
                                                              zone="inline"
                                                              value={
                                                                  k.signal
                                                                      .flagged_at
                                                              }
                                                              empty="—"
                                                          />
                                                      </>
                                                  ),
                                              }
                                            : null,
                                        k.signal.flag_expires_at
                                            ? {
                                                  label: 'Flag expires',
                                                  value: (
                                                      <DateTime
                                                          zone="inline"
                                                          value={
                                                              k.signal
                                                                  .flag_expires_at
                                                          }
                                                          empty="—"
                                                      />
                                                  ),
                                              }
                                            : null,
                                        {
                                            label: 'Attempts (1 h / 24 h)',
                                            value: `${k.signal.attempts_1h} / ${k.signal.attempts_24h}`,
                                            num: true,
                                        },
                                        {
                                            label: 'Declines (1 h / 24 h)',
                                            value: `${k.signal.declines_1h} / ${k.signal.declines_24h}`,
                                            num: true,
                                        },
                                        {
                                            label: 'Approvals (7 days)',
                                            value: (
                                                <>
                                                    {k.signal.approvals_7d} (
                                                    <Money
                                                        cents={
                                                            k.signal
                                                                .approved_cents_7d
                                                        }
                                                        currency="USD"
                                                    />
                                                    )
                                                </>
                                            ),
                                            num: true,
                                        },
                                        {
                                            label: 'Refunds (30 days)',
                                            value: k.signal.refunds_30d,
                                            num: true,
                                        },
                                        {
                                            label: 'Chargebacks (180 days)',
                                            value: k.signal.chargebacks_180d,
                                            num: true,
                                        },
                                        {
                                            label: 'Failed 3-D Secure (24 h)',
                                            value: k.signal.three_ds_failed_24h,
                                            num: true,
                                        },
                                        {
                                            label: 'First seen',
                                            value: (
                                                <DateTime
                                                    zone="inline"
                                                    value={
                                                        k.signal
                                                            .first_seen_at ??
                                                        null
                                                    }
                                                    empty="—"
                                                />
                                            ),
                                        },
                                    ]}
                                />
                            ) : (
                                <p className="text-fg-2 text-sm">
                                    No counters yet: this key has not been seen
                                    on a payment.
                                </p>
                            )}
                        </Section>
                        <Section
                            title="Identity graph"
                            description={`Keys seen together with this one, to two hops. A dashed key has more than ${k.graph.degree_cap} links and spreads nothing.`}
                        >
                            <GraphCanvas
                                nodes={k.graph.nodes}
                                edges={k.graph.edges}
                                label={`Identity graph of this ${label.toLowerCase()} key`}
                                onSelect={(n) =>
                                    navigate(keyHref(n.kind, n.key))
                                }
                            />
                        </Section>
                        <Section title="Cases">
                            {k.cases.length === 0 ? (
                                <p className="text-fg-2 text-sm">
                                    No case carries this key.
                                </p>
                            ) : (
                                <ul className="m-0 list-none space-y-1 p-0 text-sm">
                                    {k.cases.map((c) => (
                                        <li
                                            key={c.id}
                                            className="flex flex-wrap items-center gap-2"
                                        >
                                            <Link
                                                to={caseHref(c.id)}
                                                className="font-mono text-xs hover:underline"
                                            >
                                                {c.id}
                                            </Link>
                                            <StatusBadge
                                                status={c.state}
                                                domain={ADMIN_STATUSES.riskCase}
                                            />
                                            <Money
                                                cents={c.amount_cents}
                                                currency={c.currency}
                                            />
                                            <DateTime
                                                value={c.opened_at}
                                                empty="—"
                                            />
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </Section>
                    </div>
                )}
            </DetailBody>
            {dialog === 'flag' ? (
                <ConfirmDialog
                    open
                    onCancel={() => setDialog(null)}
                    title={`Flag this ${label.toLowerCase()} key`}
                    body="A flagged key is declined by the negative-list rule at its next payment."
                    confirmLabel="Flag"
                    destructive
                    loading={flag.isPending}
                    reason={{ required: true, enforce: true }}
                    error={
                        flag.error
                            ? 'The flag was not saved. Try again.'
                            : undefined
                    }
                    onConfirm={({ reason }) =>
                        flag.mutate(
                            { reason, spread },
                            { onSuccess: () => setDialog(null) },
                        )
                    }
                >
                    <div className="flex items-center gap-2">
                        <Checkbox
                            id="flag-spread"
                            checked={spread}
                            onCheckedChange={(v) => setSpread(v === true)}
                        />
                        <Label htmlFor="flag-spread">
                            Also raise the score of keys linked to it
                        </Label>
                    </div>
                </ConfirmDialog>
            ) : null}
            {dialog === 'clear' ? (
                <ReasonDialog
                    open
                    onOpenChange={(o) => (o ? null : setDialog(null))}
                    title={`Clear the flag on this ${label.toLowerCase()} key`}
                    description="The key is no longer declined by the negative-list rule."
                    confirmLabel="Clear flag"
                    required={false}
                    busy={flag.isPending}
                    error={flag.error}
                    onConfirm={() =>
                        flag.mutate(
                            { reason: null, spread: true },
                            { onSuccess: () => setDialog(null) },
                        )
                    }
                />
            ) : null}
        </div>
    );
}
